import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { enrichVerseData } from '../utils/databaseEnrich';
import { Link, useNavigate } from 'react-router-dom';

const TESTAMENTS = [
  { value: '', label: 'All Testaments' },
  { value: 'ot', label: 'Old Testament' },
  { value: 'nt', label: 'New Testament' }
];

const OT_BOOKS = [
  'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy', 'Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel',
  '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles', 'Ezra', 'Nehemiah', 'Esther', 'Job', 'Psalms', 'Proverbs',
  'Ecclesiastes', 'Song of Solomon', 'Isaiah', 'Jeremiah', 'Lamentations', 'Ezekiel', 'Daniel', 'Hosea', 'Joel',
  'Amos', 'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk', 'Zephaniah', 'Haggai', 'Zechariah', 'Malachi'
];
const NT_BOOKS = [
  'Matthew', 'Mark', 'Luke', 'John', 'Acts', 'Romans', '1 Corinthians', '2 Corinthians', 'Galatians', 'Ephesians',
  'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians', '1 Timothy', '2 Timothy', 'Titus', 'Philemon',
  'Hebrews', 'James', '1 Peter', '2 Peter', '1 John', '2 John', '3 John', 'Jude', 'Revelation'
];

function getTestament(book) {
  if (OT_BOOKS.includes(book)) return 'ot';
  if (NT_BOOKS.includes(book)) return 'nt';
  return '';
}

const normalizeRef = (ref) => (ref || '').toLowerCase().trim().replace(/\s+/g, ' ');

export default function SearchBibleVerses() {
  const [verses, setVerses] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [testament, setTestament] = useState('');
  const [book, setBook] = useState('');
  const [chapter, setChapter] = useState('1');
  const [keyword, setKeyword] = useState('');
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [page, setPage] = useState(1);
  const [systemVerseMap, setSystemVerseMap] = useState({}); // { normalizedRef: VERid }
  const PAGE_SIZE = 25;
  const navigate = useNavigate();

  // Fetch all system verses for duplicate prevention and linking
  useEffect(() => {
    async function fetchSystemVerses() {
      const versesRef = collection(db, 'verses');
      const snapshot = await getDocs(versesRef);
      const map = {};
      snapshot.docs.forEach(docSnap => {
        const ref = docSnap.data().reference;
        if (ref) map[normalizeRef(ref)] = docSnap.id;
      });
      setSystemVerseMap(map);
    }
    fetchSystemVerses();
  }, []);

  // Fetch verses from the default translation (web)
  useEffect(() => {
    async function fetchVerses() {
      setLoading(true);
      const versesRef = collection(db, 'bible/web/verses');
      const snapshot = await getDocs(versesRef);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVerses(data);
      setLoading(false);
    }
    fetchVerses();
  }, []);

  // Compute available chapters for the selected book
  const availableChapters = React.useMemo(() => {
    if (!book) return [];
    const chaptersSet = new Set();
    verses.forEach(v => {
      // Extract book and chapter
      const refParts = v.reference.split(' ');
      let bookName = refParts[0];
      if (["1", "2", "3"].includes(bookName)) {
        bookName = refParts.slice(0, 2).join(' ');
      }
      // Special case for Psalms/Psalm
      if ((book === "Psalms" && bookName === "Psalm") || (book === "Psalm" && bookName === "Psalms")) {
        bookName = book;
      }
      if (bookName === book || (book === "Psalms" && bookName === "Psalm") || (book === "Psalm" && bookName === "Psalms")) {
        const chapterVerse = refParts[refParts.length - 1];
        const chapterNum = chapterVerse.split(':')[0];
        chaptersSet.add(chapterNum);
      }
    });
    return Array.from(chaptersSet).sort((a, b) => Number(a) - Number(b));
  }, [verses, book]);

  // Reset chapter to 1 when book changes
  React.useEffect(() => {
    if (book) setChapter('1');
  }, [book]);

  // Filter verses
  useEffect(() => {
    let filtered = verses;
    if (testament) {
      filtered = filtered.filter(v => getTestament(v.reference.split(' ')[0]) === testament);
    }
    if (book) {
      filtered = filtered.filter(v => {
        const refBook = v.reference.split(' ')[0];
        let bookName = refBook;
        if (["1", "2", "3"].includes(bookName)) {
          bookName = v.reference.split(' ').slice(0, 2).join(' ');
        }
        // Special case for Psalms/Psalm
        if ((book === "Psalms" && bookName === "Psalm") || (book === "Psalm" && bookName === "Psalms")) {
          return true;
        }
        return bookName === book;
      });
      // Filter by chapter
      if (chapter) {
        filtered = filtered.filter(v => {
          const refParts = v.reference.split(' ');
          const chapterVerse = refParts[refParts.length - 1];
          const chapterNum = chapterVerse.split(':')[0];
          return chapterNum === chapter;
        });
      }
    }
    if (keyword) {
      const kw = keyword.toLowerCase();
      filtered = filtered.filter(v =>
        v.reference.toLowerCase().includes(kw) ||
        v.text.toLowerCase().includes(kw)
      );
    }
    setFiltered(filtered);
    setPage(1);
  }, [verses, testament, book, chapter, keyword]);

  // Sort numerically by chapter and verse before paginating
  const sorted = filtered.slice().sort((a, b) => {
    // Extract chapter and verse numbers from reference
    const getChapterVerse = (ref) => {
      const parts = ref.split(' ');
      const chapterVerse = parts[parts.length - 1]; // e.g., "1:10"
      const [chapter, verse] = chapterVerse.split(':').map(Number);
      return { chapter, verse };
    };
    const aCV = getChapterVerse(a.reference);
    const bCV = getChapterVerse(b.reference);
    if (aCV.chapter !== bCV.chapter) return aCV.chapter - bCV.chapter;
    return aCV.verse - bCV.verse;
  });
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  // Selection
  const toggleSelect = (id) => {
    setSelected(sel => ({ ...sel, [id]: !sel[id] }));
  };
  const selectAllOnPage = () => {
    const ids = paginated
      .filter(v => !systemVerseMap[normalizeRef(v.reference)])
      .map(v => v.id);
    setSelected(sel => {
      const newSel = { ...sel };
      ids.forEach(id => { newSel[id] = true; });
      return newSel;
    });
  };
  const clearSelection = () => setSelected({});

  // Add selected verses to system, skipping duplicates
  const addToSystem = async () => {
    setLoading(true);
    const toAdd = paginated.filter(v => selected[v.id] && !systemVerseMap[normalizeRef(v.reference)]);
    // Fetch all existing VER IDs
    const versesRef = collection(db, 'verses');
    const snapshot = await getDocs(versesRef);
    const existingIds = snapshot.docs.map(doc => doc.id).filter(id => /^VER\d+$/.test(id));
    let maxNum = 0;
    existingIds.forEach(id => {
      const num = parseInt(id.replace('VER', ''), 10);
      if (!isNaN(num) && num > maxNum) maxNum = num;
    });
    let nextNum = maxNum + 1;
    const newVerseIds = [];
    for (const verse of toAdd) {
      const newId = `VER${String(nextNum).padStart(5, '0')}`;
      await setDoc(doc(db, 'verses', newId), {
        ...verse,
        id: newId,
        active: true,
        enriched: false
      });
      newVerseIds.push(newId);
      nextNum++;
    }
    setLoading(false);
    // Instead of alert, redirect to enrichment page with newVerseIds
    navigate('/enrich-new-verses', { state: { newVerseIds } });
    clearSelection();
  };

  // Enrich selected verses
  const enrichSelected = async () => {
    setEnriching(true);
    const toEnrich = paginated.filter(v => selected[v.id]);
    // Only enrich if at least one is selected
    if (toEnrich.length === 0) {
      setEnriching(false);
      return;
    }
    // Use enrichVerseData for each
    for (const verse of toEnrich) {
      await enrichVerseData(null, {
        contextualMeaning: true,
        difficultyLevel: true,
        relatedVerses: true,
        tags: true,
        categories: true,
        themeMetadata: true
      }, 'all');
    }
    setEnriching(false);
    alert(`Enriched ${toEnrich.length} verses.`);
  };

  return (
    <div className="w-full px-6 md:px-12 py-8">
      <h1 className="text-2xl font-bold mb-4">Search Bible Verses</h1>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <select
          className="border rounded h-8 text-sm px-1 py-1 w-auto"
          value={testament}
          onChange={e => setTestament(e.target.value)}
        >
          {TESTAMENTS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select
          className="border rounded h-8 text-sm px-1 py-1 w-auto"
          value={book}
          onChange={e => setBook(e.target.value)}
        >
          <option value="">All Books</option>
          <optgroup label="Old Testament">
            {OT_BOOKS.map(b => <option key={b} value={b}>{b}</option>)}
          </optgroup>
          <optgroup label="New Testament">
            {NT_BOOKS.map(b => <option key={b} value={b}>{b}</option>)}
          </optgroup>
        </select>
        {/* Chapter selector always shown when a book is selected */}
        {book && availableChapters.length > 0 && (
          <select
            className="border rounded h-8 text-sm px-1 py-1 w-auto"
            value={chapter}
            onChange={e => setChapter(e.target.value)}
          >
            {availableChapters.map(ch => (
              <option key={ch} value={ch}>Ch {ch}</option>
            ))}
          </select>
        )}
        <input
          className="border rounded h-8 text-sm px-1 py-1 flex-1 min-w-[120px] w-auto"
          placeholder="Keyword search..."
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
        />
        <button className="px-2 py-1 h-8 text-sm bg-gray-200 rounded w-auto" onClick={selectAllOnPage}>Select All on Page</button>
        <button className="px-2 py-1 h-8 text-sm bg-gray-200 rounded w-auto" onClick={clearSelection}>Clear Selection</button>
      </div>
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-lg font-semibold mb-2">This may take a few seconds...</div>
          <div className="text-gray-600">We are loading in the whole Bible for you to search.</div>
        </div>
      ) : (
        <>
          <table className="w-full text-base mb-4">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left w-20">Select</th>
                <th className="text-left" style={{minWidth: '160px', whiteSpace: 'nowrap'}}>Reference</th>
                <th className="text-left">Text</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(verse => {
                const normRef = normalizeRef(verse.reference);
                const verId = systemVerseMap[normRef];
                return (
                  <tr key={verse.id} className={selected[verse.id] ? 'bg-blue-50' : ''} style={{fontSize: '1.125rem'}}>
                    <td>
                      {verId ? null : (
                        <input
                          type="checkbox"
                          checked={!!selected[verse.id]}
                          onChange={() => toggleSelect(verse.id)}
                        />
                      )}
                    </td>
                    <td>
                      {verId ? (
                        <Link
                          to={`/edit-verse/${verId}`}
                          className="text-blue-600 underline hover:text-blue-800"
                        >
                          {verse.reference}
                        </Link>
                      ) : (
                        verse.reference
                      )}
                    </td>
                    <td>{verse.text}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex justify-between items-center mb-4">
            <div>Page {page} of {totalPages}</div>
            <div className="flex gap-2">
              <button
                className="px-2 py-1 border rounded text-base"
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >Prev</button>
              <button
                className="px-2 py-1 border rounded text-base"
                disabled={page === totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >Next</button>
            </div>
          </div>
          <div className="flex gap-4">
            <button
              className="px-4 py-2 bg-green-700 text-white rounded hover:bg-green-800 text-base"
              onClick={addToSystem}
              disabled={loading}
            >
              Add Selected to System
            </button>
          </div>
        </>
      )}
    </div>
  );
} 